/**
 * Chat with your docs (DEV-6130): loan documents picked in Ask Addy stay
 * attached to the chat they were picked in, and every message in that chat
 * sends them to the agent as inline documents. Only document metadata is kept
 * per chat; the extracted text is held in memory and refetched for a chat that
 * is reopened from history.
 */

export const CHAT_DOCS_STORAGE_KEY = "addy-ai-chat-docs";
// Loan documents without a stored type are almost always PDFs.
const DEFAULT_CHAT_DOC_MIME_TYPE = "application/pdf";

/**
 * @typedef {Object} ChatDoc
 * @property {string} key - Unique across loans: `${loanId}:${id}`
 * @property {string} id
 * @property {string} loanId
 * @property {string} loanName
 * @property {string} organizationId
 * @property {string} name
 * @property {string} mimeType
 * @property {string} downloadURL
 * @property {string} previewImageURL
 */

export const getChatDocKey = ({ loanId, id }) => `${loanId || ""}:${id}`;

/**
 * @param {Object} document - A picker document (models/loanDocsModel)
 * @param {{ loanId: string, loanName?: string, organizationId?: string }} context
 * @returns {ChatDoc}
 */
export const toChatDoc = (document, { loanId, loanName = "", organizationId = "" }) => {
  const {
    id,
    name,
    mimeType = "",
    downloadURL = "",
    previewImageURL = "",
  } = document;

  return {
    key: getChatDocKey({ loanId, id }),
    id,
    loanId,
    loanName,
    organizationId,
    name,
    mimeType,
    downloadURL,
    previewImageURL,
  };
};

/** Adds newly picked documents to a chat, skipping ones it already has. */
export const mergeChatDocs = (currentDocs = [], pickedDocs = []) => {
  const seenKeys = new Set(currentDocs.map(({ key }) => key));
  const newDocs = pickedDocs.filter(({ key }) => {
    if (seenKeys.has(key)) {
      return false;
    }
    seenKeys.add(key);

    return true;
  });

  return [...currentDocs, ...newDocs];
};

export const removeChatDoc = (docs = [], key) => docs.filter((doc) => doc.key !== key);

/**
 * Applies a picker confirmation for one loan: that loan's documents become
 * exactly the picked set (unticked ones leave the chat), while documents
 * picked from other loans stay.
 */
export const replaceLoanChatDocs = (currentDocs = [], loanId, pickedDocs = []) => {
  const pickedKeys = new Set(pickedDocs.map(({ key }) => key));
  const keptDocs = currentDocs.filter(
    (doc) => doc.loanId !== loanId || pickedKeys.has(doc.key)
  );

  return mergeChatDocs(keptDocs, pickedDocs);
};

/** Document IDs already in the chat, per loan, so the picker opens with them ticked. */
export const getChatDocIdsByLoan = (docs = []) =>
  docs.reduce(
    (idsByLoan, { loanId, id }) => ({
      ...idsByLoan,
      [loanId]: [...(idsByLoan[loanId] || []), id],
    }),
    {}
  );

/**
 * Shapes a chat's documents for Ask Addy's attachment cards, so picked loan
 * documents look exactly like uploaded files above the input.
 */
export const toAttachmentFiles = (docs = []) =>
  docs.map(({ key, name, mimeType, previewImageURL, downloadURL }) => ({
    key,
    name,
    type: mimeType || DEFAULT_CHAT_DOC_MIME_TYPE,
    preview: previewImageURL || downloadURL,
  }));

/** Keys of chat documents the attachment cards no longer list (removed with ×). */
export const getRemovedChatDocKeys = (docs = [], remainingFiles = []) => {
  const remainingKeys = new Set(remainingFiles.map(({ key }) => key));

  return docs.filter(({ key }) => !remainingKeys.has(key)).map(({ key }) => key);
};

/** Returns a copy of the per-chat map with this chat's documents replaced. */
export const setChatDocsForChat = (docsByChatId = {}, chatId, docs = []) => {
  const otherChats = Object.fromEntries(
    Object.entries(docsByChatId).filter(([id]) => id !== chatId)
  );

  return docs.length > 0 ? { ...otherChats, [chatId]: docs } : otherChats;
};

export const loadChatDocsByChatId = (storage = window.localStorage) => {
  try {
    const parsed = JSON.parse(storage.getItem(CHAT_DOCS_STORAGE_KEY) || "{}");

    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const saveChatDocsByChatId = (docsByChatId, storage = window.localStorage) => {
  try {
    storage.setItem(CHAT_DOCS_STORAGE_KEY, JSON.stringify(docsByChatId));
  } catch {
    // Storage full or blocked: the documents stay attached for this session.
  }
};

/**
 * Saves one chat's documents, re-reading the stored map first so another open
 * tab's chats are never overwritten.
 */
export const saveChatDocsForChat = (chatId, docs = [], storage = window.localStorage) =>
  saveChatDocsByChatId(setChatDocsForChat(loadChatDocsByChatId(storage), chatId, docs), storage);

/** Documents whose text is not in memory yet, e.g. in a chat reopened from history. */
export const getDocsMissingText = (docs = [], textByKey = {}) =>
  docs.filter(({ key }) => typeof textByKey[key] !== "string");

/** Groups documents by the loan they belong to, so each loan's documents are fetched once. */
export const groupDocsByLoan = (docs = []) =>
  docs.reduce((groups, doc) => {
    const { loanId, organizationId } = doc;
    const group = groups[loanId] || { loanId, organizationId, docs: [] };

    return { ...groups, [loanId]: { ...group, docs: [...group.docs, doc] } };
  }, {});

/** Picked documents with their text, for matching citations back to their source. */
export const toCitationSourceDocuments = (docs = [], textByKey = {}) =>
  docs.map(({ key, id, name, downloadURL }) => ({
    id,
    name,
    url: downloadURL,
    textContent: textByKey[key] || "",
  }));

/**
 * Shapes a chat's documents as the agent's inline documents. The URL is what
 * citations open; documents without extracted text are left out because the
 * agent has nothing to read in them.
 */
export const toInlineChatDocuments = (docs = [], textByKey = {}) =>
  docs
    .filter(({ key }) => textByKey[key])
    .map(({ key, name, downloadURL }) => ({
      name,
      textContent: textByKey[key],
      url: downloadURL,
    }));
